/**
 * Due to how admin side bar package works we need a way to funnel the state to the footer (read readme in both packages for more info)
 * We also want to use AK forms <Form> component so we are compliant with <Field/> which we are using
 * Both Rss and Rxjs js banned so can only use context
 *
 * Check Create side bar for usage example
 */

import React, { useCallback, useEffect, useMemo, memo } from 'react';
import isEqual from 'lodash/fp/isEqual';
import omit from 'lodash/fp/omit';
import mapValues from 'lodash/fp/mapValues';
import type { FormState as FinalFormState } from 'final-form';
import Form, { useFormState as useAKFormState } from '@atlaskit/form';
import type { SidebarId } from './JiraFieldCategoryType.types.tsx';
import { useFormState } from './SidebarFormsProvider.tsx';
import type { FormProviderProps, FormValues } from './FormStateContext.types.tsx';

type FromState<T extends FormValues> = FinalFormState<T> & {
	isInFlight: boolean;
};

type UseSidebarFormValue<T extends FormValues> = {
	sidebarState: FromState<T> | undefined;
	setFormValues: (state: Partial<T>) => void;
	setIsInFlight: (isInFlight: boolean) => void;
	reset: () => void;
};

/**
 * Gets you the state and actions of one sidebar
 * @param sidebarId - The sidebar id
 * @returns - The sidebar state and the actions
 */
export const useSidebarForm = <T extends FormValues>(
	sidebarId: SidebarId | undefined,
): UseSidebarFormValue<T> => {
	const { state, setSidebarState, resetSidebar } = useFormState();
	const sidebarState = sidebarId ? state[sidebarId] : undefined;
	const setState = useMemo(() => setSidebarState(sidebarId), [setSidebarState, sidebarId]);
	const setIsInFlight = useCallback(
		(isInFlight: boolean) => setState?.({ isInFlight }),
		[setState],
	);
	const reset = useCallback(() => resetSidebar(sidebarId), [resetSidebar, sidebarId]);
	const setFormValues = useCallback(
		(nextState: Partial<T>) => {
			setState({ values: { ...sidebarState?.values, ...nextState } });
		},
		[setState, sidebarState?.values],
	);

	// useMemo here to allow withSidebarFormData to memoize the hook value for performance optimizations
	return useMemo(
		() => ({
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			sidebarState: sidebarState as FromState<T>,
			setIsInFlight,
			setFormValues,
			reset,
		}),
		[sidebarState, setIsInFlight, setFormValues, reset],
	);
};

/**
 * Some lower level functions, you should use useSidebarForm instead to listen to your form values
 */
export const useSubscribeToSidebarState = (sidebarId: SidebarId) => {
	const { state, setSidebarState, resetSidebar } = useFormState();
	const sidebarState = state[sidebarId];
	const setState = useMemo(() => setSidebarState(sidebarId), [setSidebarState, sidebarId]);
	const reset = useCallback(() => resetSidebar(sidebarId), [resetSidebar, sidebarId]);
	return { sidebarState, setState, reset };
};

const SubscribeToSidebarState = ({ sidebarId }: { sidebarId: SidebarId }) => {
	const { setSidebarState, resetSidebar } = useFormState();
	const setState = useMemo(() => setSidebarState(sidebarId), [setSidebarState, sidebarId]);
	const reset = useCallback(() => resetSidebar(sidebarId), [resetSidebar, sidebarId]);
	const formState = useAKFormState<FormValues>({
		pristine: true,
		dirty: true,
		dirtyFields: true,
		values: true,
		error: true,
		errors: true,
		validating: true,
		valid: true,
		invalid: true,
		touched: true,
		visited: true,
		initialValues: true,
	});

	useEffect(() => {
		if (formState) {
			// Custom handling for empty initial values arrays to fix form dirty state, unfortunately cannot handle nested options due to order not being guaranteed when changed
			const cleanValues = createCleanFormValues(formState.values);
			const isPristine = isEqual(formState.initialValues, cleanValues);
			setState({
				...formState,
				pristine: isPristine,
				dirty: !isPristine,
				dirtyFields: isPristine ? {} : formState.dirtyFields,
			});
		}
		return reset;
	}, [formState, reset, setState]);

	return null;
};

/**
 * Similar to @atlaskit/form's <Form> Wrap your Field components with this
 * @param sidebarId - The sidebar id
 * @param children - The children to render
 * @param onSubmit - The onSubmit function, when you submit the form
 * @returns
 */
export const FormProvider = <T extends Record<string, unknown>>({
	sidebarId,
	children,
	onSubmit,
}: FormProviderProps<T>) => {
	return (
		<Form onSubmit={onSubmit}>
			{({ formProps }) => (
				<>
					<form {...formProps} id={sidebarId}>
						{children}
					</form>
					<SubscribeToSidebarState sidebarId={sidebarId} />
				</>
			)}
		</Form>
	);
};

export interface WithSidebarFormDataProps {
	sidebarFormData: UseSidebarFormValue<FormValues>;
}

/**
 * Wrap your component with this to have access to the sidebar form state,
 * Used for performance optimizations reasons over using the hook useSidebarForm if you really don't want the contents to re-render when not subscribed to that sidebar
 * Your component must accept sidebarFormData as a prop
 * Check ./examples.tsx for usage
 * @param Component - The component to wrap
 * @param sidebarId - The sidebar id
 * @returns The wrapped component
 */
export const withSidebarFormData = <P extends WithSidebarFormDataProps>(
	sidebarId: SidebarId,
	Component: React.ComponentType<P>,
): React.FC<Omit<P, 'sidebarFormData'>> => {
	const MemoComponent = memo(Component);
	return (props: Omit<P, 'sidebarFormData'>) => {
		const hookValue = useSidebarForm(sidebarId);
		// @ts-expect-error - known typescript bug with HOCs https://github.com/Microsoft/TypeScript/issues/28938
		return <MemoComponent {...props} sidebarFormData={hookValue} />;
	};
};

// Helper functions

const isRecord = (value: unknown): value is Record<string, unknown> => {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const removeUuidProperties = (value: unknown): unknown => {
	if (!Array.isArray(value)) {
		return value;
	}

	return value.map((item) => {
		if (!isRecord(item)) {
			return item;
		}
		const hasUuIds = 'externalUuid' in item || 'parentExternalUuid' in item;
		const isOption = 'parentOptionId' in item && item.parentOptionId === undefined;
		const propsToOmit = ['externalUuid', 'parentExternalUuid'];
		if (isOption) propsToOmit.push('parentOptionId');

		return hasUuIds || isOption ? omit(propsToOmit, item) : item;
	});
};

/**
 * Creates a clean version of form values
 * Removes id properties that shouldn't affect dirty field comparison
 */
export const createCleanFormValues = (formValues: FormValues): FormValues =>
	mapValues(removeUuidProperties, formValues);
